import { Enquiry } from "../../components/Enquiry";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { Card, Carousel } from "@/components/layout/sections/CardCarousel";
import { FeatureShowcase, type FeatureShowcaseProps } from "@/components/layout/sections/FeatureShowcase";
import { Hero } from "@/components/layout/sections/Hero";
import ImageSection from "@/components/layout/sections/ImageSection";
import { ProductFooter } from "@/components/layout/sections/ProductFooter";
import Section from "@/components/layout/sections/Section";
import { SectionTabs } from "@/components/layout/sections/SectionTabs";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faCirclePlus, faComments, faCopy, faShuffle, faStar, faTrashCan } from "@icons/regular";
import { Button, Icon } from "@ui";
import { Link, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const features: FeatureShowcaseProps[] = [
  {
    meta: "Title section",
    title: "A bold entrance",
    description: "Create compelling titles and unforgettable first impressions with text on a full-screen image or video.",
    image: "product/sections/title-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Text Over Media section",
    title: "Words that stand out",
    description: "Seamlessly overlay large text on full-screen media, with enhanced overlay controls for readability and impact.",
    video: "product/sections/text-over-media-section.mp4",
    imagePlacement: "left",
  },
  {
    meta: "Reveal section",
    title: "Scroll to discover",
    description: "Let visuals unfold dynamically as you scroll, with cinematic image and video transitions that captivate attention.",
    image: "product/sections/reveal-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Text section",
    title: "Focus on the message",
    description:
      "Keep it clean, focused, and flexible — perfect for delivering narrative, context, or commentary with embedded assets.",
    image: "product/sections/text-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Scrollmation section",
    title: "Scroll-driven storytelling",
    description:
      "Add motion to your message with animations for foreground and full-screen imagery that evolves step-by-step as the reader scrolls.",
    image: "product/sections/scrollmation-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Scrollpoints section",
    title: "Moments that move",
    description: "Sync text and visuals precisely by setting scroll-triggered transitions that guide the reader around an image.",
    image: "product/sections/scrollpoints-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Grid section",
    title: "Structured creativity",
    description:
      "Arrange text, media, and more in customisable columns and rows — ideal for complex layouts or side-by-side comparisons.",
    image: "product/sections/grid-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Media Gallery section",
    title: "From static to stunning",
    description: "Showcase a diverse collection of media in a grid or carousel that expands to a full-screen slideshow.",
    image: "product/sections/media-gallery-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Media section",
    title: "Full-screen impact",
    description: "Embed uncropped, high-quality images or videos that fill the screen for maximum visual immersion.",
    video: "product/sections/media-section.mp4",
    imagePlacement: "left",
  },
  {
    meta: "Chart section",
    title: "Data that tells a story",
    description: "Transform numbers into narratives with dynamic charts and text blocks — perfect for powerful data storytelling.",
    image: "product/sections/chart-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Custom HTML section",
    title: "Total creative control",
    description: "Go beyond the template. Add custom elements, embeds, or code for tailored functionality and advanced design.",
    image: "product/sections/custom-html-section.png",
    imagePlacement: "left",
  },
  {
    meta: "Collection section",
    title: "Curated content at a glance",
    description: "Display a grid of content with thumbnails, guiding readers to explore more of your work in a clean, visual layout.",
    image: "product/sections/collection-section.png",
    imagePlacement: "left",
  },
];

const modifyFeatures = [
  {
    id: 1,
    icon: faCirclePlus,
    description: "Add",
  },
  {
    id: 2,
    icon: faCopy,
    description: "Copy",
  },
  {
    id: 3,
    icon: faShuffle,
    description: "Convert",
  },
  {
    id: 4,
    icon: faStar,
    description: "Save",
  },
  {
    id: 5,
    icon: faComments,
    description: "Comment",
  },
  {
    id: 6,
    icon: faTrashCan,
    description: "Delete",
  },
];

const FeaturesSections: React.FC<{ location: any; data: any }> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    setShowPortal(true);
    prePopulatedFields.current = fields || {};
  }

  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  const cards = props.data.allFeaturedStoriesJson.nodes.map((story: any, index: number) => (
    <Card
      key={story.linkUrl || index}
      card={{
        id: index + 1,
        title: story.title,
        imageUrl: story.imageUrl?.src?.publicURL || story.imageUrl?.src?.childImageSharp?.gatsbyImageData?.images?.fallback?.src || "",
        linkUrl: story.linkUrl,
      }}
    />
  ));

  return (
    <Layout location={props.location} theme="dark-theme">
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-features body-light hide-elevio",
          }}
        ></Helmet>
        <Hero
          title={
            <>
              Build in seconds with <span className="tw-font-PassengerSerif tw-italic">sections</span>
            </>
          }
          description="Your content deserves to be stunning. Shorthand gives you the ultimate storytelling toolkit — beautiful, intuitive, and flexible sections designed to give you visual superpowers."
          image={
            <StaticImage
              src={`../../../static/product/sections/sections-hero.png`}
              alt="App screenshot"
              width={2432}
              height={1442}
              className="tw-w-[76rem] tw-rounded-md tw-bg-white/5 tw-shadow-2xl tw-ring-1 tw-ring-white/10"
            />
          }
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />
        <SectionTabs
          title={
            <>
              Explore our <span className="tw-font-PassengerSerif tw-italic">building blocks</span>.
            </>
          }
          description="Curate and craft immersive, interactive experiences with Shorthand sections. Whether you're building a sleek brand story, an engaging editorial, or a data-driven narrative, these sections are the secret sauce to making it all happen."
          disableMinHeight
        >
          {features.map(feature => (
            <Section key={`feature-showcase-${feature.meta}`} data-section={`feature-showcase-${feature.meta}`} className="tw-py-4">
              <FeatureShowcase
                preventOverflow
                {...(feature.video
                  ? {
                      meta: feature.meta,
                      title: feature.title,
                      description: feature.description,
                      video: feature.video,
                      features: feature.features,
                      imagePlacement: feature.imagePlacement,
                    }
                  : {
                      meta: feature.meta,
                      title: feature.title,
                      description: feature.description,
                      image: feature.image,
                      features: feature.features,
                      imagePlacement: feature.imagePlacement,
                    })}
              />
            </Section>
          ))}
        </SectionTabs>
        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                Creative control <br />
                without the complexity
              </>
            }
            description="With Shorthand’s sections, you’re in control. Easily add, copy, save, duplicate, delete, split, and convert sections to shape your story exactly how you want. Start with any section and make it uniquely yours."
          />
          <ImageSection image="features/editor-screenshot.png" imageAlt="Editor screenshot">
            <div className="tw-mt-16 tw-grid tw-grid-cols-3 tw-gap-y-8 sm:tw-grid-cols-6 sm:tw-gap-y-0">
              {modifyFeatures.map(feature => (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4" key={`modify-feature-${feature.id}`}>
                  <Icon icon={feature.icon} className="tw-size-6 tw-text-white sm:tw-size-7 lg:tw-size-8" />
                  <span className="tw-max-w-[15ch] tw-text-center tw-text-[var(--foreground-color-muted)]">{feature.description}</span>
                </div>
              ))}
            </div>
          </ImageSection>
        </Section>

        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                See it in <span className="tw-font-PassengerSerif tw-italic">action</span>
              </>
            }
            description="Not sure where to start? Browse our story examples to see how these sections come together to create something truly special. Get inspired to craft beautifully engaging content that gets results."
          />
          <Carousel items={cards} />
        </Section>

        <ProductFooter />
        <Footer className="mt-0" />
      </main>
    </Layout>
  );
};

export default FeaturesSections;

export const query = graphql`
  query FeaturesSections {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
    allFeaturedStoriesJson {
      nodes {
        title
        linkUrl
        imageUrl {
          alt
          src {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, width: 600, height: 830, quality: 80)
            }
            publicURL
          }
        }
      }
    }
  }
`;
